import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CheckSquare, Square, Plus, Trash, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PreServiceChecklist({ isOpen, onClose }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [showTemplateEdit, setShowTemplateEdit] = useState(false);
  const isAdmin = user?.role === "admin";

  const fetchChecklist = useCallback(async () => {
    try {
      const { data } = await api.get("/checklist/today");
      setItems(data);
    } catch { /* silent */ }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/checklist/template");
      setTemplates(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (isOpen) { fetchChecklist(); fetchTemplates(); }
  }, [isOpen, fetchChecklist, fetchTemplates]);

  // WebSocket
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    if (!wsUrl || !isOpen) return;
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "checklist_update") fetchChecklist();
      };
    } catch { /* silent */ }
    return () => ws?.close();
  }, [isOpen, fetchChecklist]);

  const toggleItem = async (item) => {
    try {
      await api.put(`/checklist/today/${item.id}`, { checked: !item.checked });
      fetchChecklist();
    } catch { toast.error("Failed"); }
  };

  const addTemplateItem = async () => {
    if (!newItem.trim()) return;
    try {
      await api.post("/checklist/template", { text: newItem.trim(), order: templates.length });
      setNewItem("");
      fetchTemplates();
      toast.success("Item added to template");
    } catch { toast.error("Failed"); }
  };

  const deleteTemplateItem = async (id) => {
    try {
      await api.delete(`/checklist/template/${id}`);
      fetchTemplates();
      toast.success("Removed from template");
    } catch { toast.error("Failed"); }
  };

  const checkedCount = items.filter((i) => i.checked).length;
  const progress = items.length > 0 ? Math.round((checkedCount / items.length) * 100) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="checklist-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-stone-800" style={{ fontFamily: "Outfit" }}>
            <ListChecks size={20} weight="duotone" className="text-[#2F4F4F]" />
            Pre-Service Checklist
          </DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="mt-2 mb-4">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-stone-500">{checkedCount} of {items.length} complete</span>
            <span className="font-mono font-medium" style={{ color: progress === 100 ? "#81B29A" : "#2F4F4F" }}>{progress}%</span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-stone-50">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, backgroundColor: progress === 100 ? "#81B29A" : "#2F4F4F" }}
            />
          </div>
        </div>

        {/* Checklist Items */}
        <div className="space-y-1.5 max-h-[320px] overflow-y-auto" data-testid="checklist-items">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => toggleItem(item)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                item.checked
                  ? "border-green-500/20 bg-green-500/5"
                  : "border-stone-100 bg-stone-50 hover:bg-stone-100"
              }`}
              data-testid={`checklist-item-${item.id}`}
            >
              {item.checked ? (
                <CheckSquare size={20} weight="fill" className="text-green-500 flex-shrink-0" />
              ) : (
                <Square size={20} className="text-stone-300 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <span className={`text-sm ${item.checked ? "text-stone-500 line-through" : "text-stone-800"}`}>
                  {item.text}
                </span>
                {item.checked && item.checked_by && (
                  <p className="text-[10px] text-stone-300 mt-0.5">{item.checked_by}</p>
                )}
              </div>
            </button>
          ))}
          {items.length === 0 && (
            <p className="text-sm text-stone-300 text-center py-6">No checklist items. {isAdmin ? "Add items below." : "Ask admin to set up the checklist."}</p>
          )}
        </div>

        {/* Admin: Edit Template */}
        {isAdmin && (
          <div className="mt-4 pt-4 border-t border-stone-100">
            <button
              onClick={() => setShowTemplateEdit(!showTemplateEdit)}
              className="text-xs text-[#2F4F4F] hover:text-[#2F4F4F] transition-colors"
              data-testid="edit-template-btn"
            >
              {showTemplateEdit ? "Hide Template Editor" : "Edit Checklist Template"}
            </button>
            {showTemplateEdit && (
              <div className="mt-3 space-y-2">
                {templates.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 text-sm">
                    <span className="flex-1 text-stone-600">{t.text}</span>
                    <button onClick={() => deleteTemplateItem(t.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-template-${t.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="New checklist item..."
                    className="flex-1 bg-white border-stone-200 text-stone-800 text-sm"
                    onKeyDown={(e) => e.key === "Enter" && addTemplateItem()}
                    data-testid="new-template-input"
                  />
                  <Button onClick={addTemplateItem} size="sm" style={{ backgroundColor: "#2F4F4F" }} data-testid="add-template-btn">
                    <Plus size={14} />
                  </Button>
                </div>
                <p className="text-[10px] text-stone-300">Template changes apply to future days. Today's list is already created.</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
